import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import CustomDropdown from 'components/Dropdown';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoIcon from '@mui/icons-material/Info';
import { useTheme } from '@mui/material/styles';
import { InfoTooltip } from 'components/Widgets/InfoTooltip';

type PieChartData = {
    name: string;
    value: number;
    isUtilized?: boolean;
    count?: number;
};

type PieChartProps = {
    isLoading?: boolean;
    title: string | React.ReactNode;
    data: PieChartData[];
    colorMapping?: { [key: string]: string };
    hasDropdown?: boolean;
    hasTooltip?: boolean;
    tooltipText?: string;
    isTextTooltip?: boolean;
    dropdownValue?: string;
    dropdownOptions?: { label: string; value: string }[];
    onDropdownChange?: (value: string) => void;
    showDollarAmounts?: boolean;
    showUtilizationPercentage?: boolean;
    containerProps?: any;
};

export const renderTooltipIconComponent = (tooltipText: string, tooltipColor?: string) => {
    return (
        <InfoTooltip
            label={tooltipText}
            {...(tooltipColor ? { icon: <InfoIcon sx={{ color: tooltipColor, fontSize: '16px' }} /> } : {})}
        />
    );
};

const PieChart = ({
    isLoading = false,
    title,
    data = [],
    colorMapping = {},
    hasDropdown = false,
    hasTooltip = false,
    isTextTooltip = false,
    tooltipText,
    dropdownValue,
    dropdownOptions = [],
    onDropdownChange,
    showDollarAmounts = false,
    showUtilizationPercentage = false,
    containerProps
}: PieChartProps) => {
    const theme = useTheme();
    const colors = theme.palette;
    const defaultColors = [
        colors.widget?.colors?.color1 || '#4A90E2',
        colors.widget?.colors?.color6 || '#01C008',
        colors.widget?.colors?.trendLine || '#FFA500',
        colors.widget?.colors?.color4 || '#9B59B6',
        colors.widget?.colors?.color9 || '#95A5A6',
        colors.widget?.text?.changeUp || '#E74C3C',
        colors.widget?.colors?.color2 || '#3498DB'
    ];

    // Calculate totals for percentage display
    const totalAll = data.reduce((sum, item) => sum + item.value, 0);

    const chartOption: any = {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'item' as const,
            backgroundColor: colors.widget?.chart?.tooltipBg,
            borderColor: colors.widget?.chart?.legendInactiveUpDownIconColor,
            borderWidth: 1,
            textStyle: {
                color: colors.widget?.chart?.tooltipText,
                fontSize: 12
            },
            formatter: (params: any) => {
                const count = params.data?.count;
                if (showUtilizationPercentage) {
                    // For utilization chart, value is already a percentage
                    const baseText = `${params.name}<br/>Utilization: ${params.value.toFixed(1)}%`;
                    if (typeof count === 'number') {
                        return `${baseText}<br/>Count: ${count.toLocaleString()}`;
                    }
                    return baseText;
                }
                const percent = totalAll > 0 ? ((params.value / totalAll) * 100).toFixed(1) : 0;
                const baseText = `${params.name}: ${params.value.toLocaleString()} (${percent}%)`;
                if (typeof count === 'number') {
                    return `${baseText}<br/>Count: ${count.toLocaleString()}`;
                }
                return baseText;
            }
        },
        legend: {
            show: true,
            type: 'scroll',
            orient: 'horizontal',
            bottom: '0px',
            left: 'center',
            itemWidth: 14,
            itemHeight: 14,
            itemGap: 15,
            textStyle: {
                color: colors.widget?.chart?.legendTextColor,
                fontSize: 11
            },
            pageTextStyle: {
                color: colors.widget?.chart?.legendTextColor,
                fontSize: 12,
                fontWeight: 500
            },
            pageIconColor: colors.widget?.chart?.legendActiveUpDownIconColor,
            pageIconInactiveColor: colors.widget?.chart?.legendInactiveUpDownIconColor, 
            pageIconSize: 12,
            data: data.map(item => item.name)
        },
        series: [
            {
                name: 'License Distribution',
                type: 'pie',
                radius: '70%',
                center: ['50%', '45%'],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 0,
                    borderColor: colors.widget?.background?.card,
                    borderWidth: 0
                },
                label: {
                    show: true,
                    position: 'inside',
                    formatter: (params: any) => {
                        if (showDollarAmounts) {
                            const value = params.value;
                            if (value >= 1000000) {
                                const millions = value / 1000000;
                                return `${millions.toFixed(2)} MM`;
                            } else if (value >= 1000) {
                                const thousands = value / 1000;
                                return `${thousands.toFixed(2)} K`;
                            }
                            return `${value.toLocaleString()}`;
                        }

                        if (showUtilizationPercentage) {
                            // For utilization, value is already a percentage
                            return `${params.value.toFixed(1)}%`;
                        }

                        const percent = totalAll > 0 ? ((params.value / totalAll) * 100).toFixed(1) : 0;
                        return `${percent}%`;
                    },
                    color: colors.widget?.chart?.labelColor,
                    fontSize: 12,
                    fontWeight: 'bold'
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 12,
                        fontWeight: 'bold'
                    }
                },
                data: data.map((item, index) => ({
                    value: item.value,
                    name: item.name,
                    count: item.count,
                    itemStyle: {
                        color: colorMapping[item.name] || defaultColors[index % defaultColors.length]
                    }
                }))
            }
        ]
    };

    const defaultOption = {
        backgroundColor: colors.widget?.background?.card,
        graphic: [
            {
                type: 'text',
                left: 'center',
                top: 'center',
                style: {
                    text: 'No data',
                    fill: colors.widget?.text?.noChange,
                    fontSize: 12
                }
            }
        ]
    };

    const renderTooltipIcon = () => {
        if (!hasTooltip) return null;

        return renderTooltipIconComponent(tooltipText || '');
    };

    return (
        <Box display="flex" flexGrow={1} height="100%" position="relative" {...containerProps}>
            <Stack
                minHeight="100px"
                p={2}
                bgcolor={colors.widget?.background?.card}
                fontFamily="Montserrat"
                borderRadius="5px"
                flexGrow={1}
                sx={{
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    color: `${colors.widget?.text?.main} !important`,
                    ...containerProps?.sx
                }}
            >
                <Stack
                    direction="row"
                    gap={2}
                    justifyContent="space-between"
                    alignItems="center"
                    minHeight="40px"
                >
                    <Typography variant="widgetTitle" sx={{ color: colors.widget?.text?.title, display: 'flex', alignItems: 'center', gap: 1 }}>
                        {typeof title === 'string' ? <HighlightedText text={title} /> : title}
                        {isTextTooltip && renderTooltipIcon()}
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={1}>
                        {!isTextTooltip && renderTooltipIcon()}
                        {hasDropdown && dropdownValue && dropdownOptions.length > 0 && (
                            <CustomDropdown
                                value={dropdownValue}
                                options={dropdownOptions}
                                handleChange={(event: any) => {
                                    if (onDropdownChange) {
                                        onDropdownChange(event.target.value);
                                    }
                                }}
                                dropdownIcon={KeyboardArrowDownIcon}
                                selectedTextColor={colors.DropdownV2?.dropdownIconDefault}
                                textColor={colors.DropdownV2?.menuItemText}
                                iconColor={colors.DropdownV2?.dropdownIconDefault}
                                bgColor={colors.DropdownV2?.menuItemBackground}
                                borderColor="transparent"
                                hoverBgColor={colors.DropdownV2?.menuItemHover}
                                hoverTextColor={colors.DropdownV2?.menuItemHoverText}
                                selectedBgColor={colors.DropdownV2?.menuItemSelectedBg}
                                boxProps={{
                                sx: {
                                    backgroundColor: 'transparent !important',
                                    width: 'fit-content',
                                    minWidth: 'unset',
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'transparent !important',
                                        width: 'fit-content',
                                        minWidth: 'unset'
                                    },
                                    '& .MuiSelect-select': {
                                        fontFamily: 'Montserrat',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        padding: '0 24px 0 8px !important',
                                        minHeight: '28px !important',
                                        width: 'auto',
                                        minWidth: 'unset',
                                        display: 'flex',
                                        alignItems: 'center'
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none !important'
                                    },
                                    '& .MuiSelect-icon': {
                                        color: `${colors.DropdownV2?.dropdownIconDefault} !important`,
                                        right: 0
                                    }
                                }
                            }}
                            />
                        )}
                    </Stack>
                </Stack>

                <Box flexGrow={1} minHeight="300px" position="relative">
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <LoaderComponent />
                        </Box>
                    ) : (
                        <ReactECharts
                            option={data.length > 0 ? chartOption : defaultOption}
                            style={{ width: '100%', height: '100%' }}
                            opts={{ renderer: 'canvas', width: 'auto', height: 'auto' }}
                            notMerge={true}
                            lazyUpdate={true}
                        />
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

export default PieChart;
